import { fetcher } from '@commenda/utils';

import { JobStatus } from '../../new-sales-tax/job/types';
import { IIndirectTaxGenerateExposureMutationResult } from './types';

/**
 * Enqueue a v2 exposure sync. Returns immediately (HTTP 202 from upstream).
 *
 * On 409 (sync already in progress), this still resolves with `success: true` and the
 * existing job id so the caller can subscribe to the in-flight job naturally via the
 * jobs list polling hook. Polling derives state from the server every refresh — no
 * frontend job_id state.
 */
export async function generateIndirectTaxExposure(
  corporationId: number,
  options: { syncLocations?: boolean } = {}
): Promise<IIndirectTaxGenerateExposureMutationResult> {
  try {
    const params = new URLSearchParams({ corporationId: String(corporationId) });
    if (options.syncLocations) params.set('syncLocations', 'true');

    const response = await fetcher(
      `new-sales-tax/exposure/generate?${params.toString()}`,
      { method: 'POST' }
    );

    if (response.status === 409) {
      // 409 carries the existing job_id; treat as success since the caller's intent
      // (kick off a sync) is satisfied by the in-flight job.
      const errorBody = await response.json().catch(() => null);
      const existingJobId =
        errorBody?.error?.detail?.existing_job_id ??
        errorBody?.error?.detail?.existingJobId;
      return {
        success: true,
        jobId: existingJobId,
        status: JobStatus.PENDING,
        message:
          errorBody?.error?.title ??
          'Exposure sync already in progress for this corporation.',
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      return {
        success: false,
        error:
          errorData?.error?.title ?? errorData?.message ?? response.statusText,
      };
    }

    const json = await response.json();
    return {
      success: true,
      message: json?.message,
      jobId: json?.data?.job_id ?? json?.jobId,
      status: (json?.data?.status ?? json?.status) as JobStatus | undefined,
    };
  } catch (error: unknown) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to generate exposure',
    };
  }
}
